import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { UserStore } from '../app-state/user.store';
import {
  PreferencesStore,
  DEFAULT_PREFERENCES,
} from '../app-state/preferences.store';
import { AuthService } from './auth/auth.service';
import { InactivityService } from './inactivity.service';
import { MaintenanceService } from './maintenance.service';

@Injectable({
  providedIn: 'root',
})
export class AppInitService {
  private readonly userStore = inject(UserStore);
  private readonly preferencesStore = inject(PreferencesStore);
  private readonly authService = inject(AuthService);
  private readonly inactivityService = inject(InactivityService);
  private readonly maintenanceService = inject(MaintenanceService);
  private readonly router = inject(Router);

  async initializeApp(): Promise<void> {
    // Patient questionnaire is standalone — do not restore app session or start inactivity
    if (this.router.url.includes('patient-questionnaire')) {
      return;
    }

    // Check maintenance status first (reads from environment variable)
    const isMaintenance = this.maintenanceService.checkMaintenanceStatus();

    // Store maintenance status in localStorage for app component to access
    localStorage.setItem('maintenanceMode', String(isMaintenance));

    // If in maintenance mode, skip user initialization
    if (isMaintenance) {
      return;
    }

    const token = localStorage.getItem('token');

    if (token) {
      // Set loading state
      this.userStore.setUser({
        user: null,
        organizationUser: null,
        organization: null,
        token: token,
        isLoading: true,
        error: false,
        role: null,
        authApp2FAenabled: null,
        email2FAEnabled: null,
      });
      try {
        // Fetch user data to restore state
        const response = await firstValueFrom(this.authService.getUser());
        if (response?.data) {
          this.userStore.setUser({
            ...response.data,
            isLoading: false,
            error: false,
            authApp2FAenabled: null,
            email2FAEnabled: null,
          });
          this.preferencesStore.loadPreferences(DEFAULT_PREFERENCES);
          // Start inactivity monitoring after successful user data restoration
          this.inactivityService.start();
        } else {
          // No data received, reset state
          this.userStore.reset();
          localStorage.removeItem('token');
        }
      } catch (error) {
        console.error('Error restoring user state:', error);
        // Clear invalid token and reset state
        this.userStore.setUser({
          user: null,
          organizationUser: null,
          organization: null,
          token: null,
          role: null,
          isLoading: false,
          authApp2FAenabled: null,
          email2FAEnabled: null,
          error: false,
        });
        localStorage.removeItem('token');
      }
    } else {
      // No token found, ensure user store is in a clean state
      this.userStore.setUser({
        user: null,
        organizationUser: null,
        organization: null,
        token: null,
        role: null,
        isLoading: false,
        error: false,
        authApp2FAenabled: null,
        email2FAEnabled: null,
      });
    }
  }
}
